import merge from "lodash/merge"
import { computed, nextTick, reactive, ref, watch } from "vue"

import { type Column, type FilterableColumn } from "@/types"
import { type Table, type TableFilterTable } from "@/graphql/types"
import { useSessionStore } from "@/store/session"
import { useColumnPreset } from "@/store/tableColumnPreset"
import { useFilterPreset } from "@/store/tableFilterPreset"
import { buildNestedObject, objectToGQLString } from "@/utils/gqlbuilder"

export type TableConfiguration<TData, TMappedData = TData> = ReturnType<
  typeof usePreset<TData, TMappedData>
>

export function usePreset<TData, TMappedData = TData>(
  table: Table | undefined,
  columnDefs: Column<TData, TMappedData>[],
  listRouteName: string | string[],
  tableFilter?: TableFilterTable | undefined,
  filterableColumnDefs?: FilterableColumn<TData, TMappedData>[]
) {
  const sessionStore = useSessionStore()

  const { column } = useColumnPreset<TData, TMappedData>(table, columnDefs, listRouteName)
  const { filter } = useFilterPreset<TData, TMappedData>(
    tableFilter,
    filterableColumnDefs ?? [],
    listRouteName
  )

  const requestedFields = ref<Set<string>>(new Set())
  const queryFields = ref<string>("")
  const queryFieldsLoaded = computed(() => queryFields.value !== "")

  const queryInfo = reactive({
    queryFields,
    queryFieldsLoaded,
    requestedFields,
  })

  const isLoaded = computed(() => column.isPresetLoaded && filter.isPresetLoaded)

  function updateQueryFields(addFields: string[] = [], client = true) {
    if (!column.isPresetLoaded) throw new Error("Column Preset not fetched yet!")
    if (!filter.isPresetLoaded) throw new Error("Filter Preset not fetched yet!")

    void nextTick(() => {
      const fields = new Set([
        ...column.selectedColumnFields,
        ...(filterableColumnDefs ?? [])
          .filter(filter.columnsFilter)
          .flatMap((c) => c.fields ?? [c.dataKey]),
        // ...filter.selectedColumnFields,
        ...addFields,
      ])
      if (fields.size === 0) return
      if (client && [...fields].every((f) => requestedFields.value.has(f))) return

      const oldFields = buildNestedObject(requestedFields.value, client)
      const newFields = buildNestedObject([...fields].filter((f) => !requestedFields.value.has(f)))

      queryFields.value = objectToGQLString(merge(oldFields, newFields))
      requestedFields.value = fields
    })
  }

  // resets the query by removing the @client directive
  function resetQuery() {
    updateQueryFields([], true)
  }

  const gqlFields = computed(() => {
    const { selectedColumnFields: pColumnFields } = column
    // const { selectedColumnFields: pFilterFields } = filter

    const fields = [
      ...(pColumnFields ?? []),
      ...(filterableColumnDefs ?? []).flatMap((c) => c.fields ?? [c.dataKey]),
      // ...(pFilterFields ?? []),
    ]

    return new Set(fields)
  })

  watch(
    [isLoaded, gqlFields],
    ([loadedVal]) => {
      if (loadedVal) updateQueryFields()
    },
    { deep: true }
  )

  watch(
    () => sessionStore.institutionFilter,
    (context) => {
      if (context && context?.institution && queryFieldsLoaded.value) updateQueryFields([], false)
    },
    { deep: true }
  )

  return {
    column,
    filter,
    queryInfo,

    isLoaded,

    updateQueryFields,
    resetQuery,
  }
}
